import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { SeattleGameState } from '../../types';
import { moveGoodsSeattle } from '../moves/moveGoodsSeattle';
import { upgradeLoco } from '../moves/upgradeLoco';
import { passMoveGoods } from '../moves/passMoveGoods';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 5: 商品輸送フェーズ（Seattle固有）
 *
 * Seattle では貨物輸送レベル回（2-5回）輸送可能。
 * Downtown/Airport への輸送時は「取り除く」「留置する」を選択可能。
 *
 * Issue #9 + Technical Debt #1: 輸送回数の共通化
 * - 共通 passMoveGoods を使用（turnsCompletedThisPhase をカウント）
 * - maxDeliveriesThisPhase で上限を設定
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_05_move_goods_seattle.md
 */
export const moveGoodsSeattlePhase: PhaseConfig<SeattleGameState> = {
  moves: {
    moveGoods: moveGoodsSeattle,
    upgradeLoco: upgradeLoco as any,
    passMoveGoods: passMoveGoods as any,  // 共通 Move を使用
    undoToTurnStart: undoToTurnStart as any,
  },

  onBegin: ({ G }: { G: SeattleGameState }) => {
    debug.log(`[moveGoodsSeattlePhase] Phase 5: Move Goods started (Turn ${G.actualTurn})`);

    // Seattle 固有: maxDeliveriesThisPhase を cargoTransportLevel と同期
    G.maxDeliveriesThisPhase = G.cargoTransportLevel;

    const player = G.players['0'];

    // Phase 5 状態をリセット（共通カウンタを使用）
    player.hasUpgradedLocoThisTurn = false;
    player.goodsTransportedThisPhase = 0;
    player.turnsCompletedThisPhase = 0;
    player.hasPassed = false;

    debug.log(`[moveGoodsSeattlePhase] Player can transport up to ${G.maxDeliveriesThisPhase} times`);
  },

  endIf: ({ G }: { G: SeattleGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log(`[moveGoodsSeattlePhase] Force transition to issueShares (Undo)`);
      return true;
    }

    // 輸送回数が上限に達したら Phase 終了（共通カウンタを使用）
    const player = G.players['0'];
    const maxDeliveries = G.maxDeliveriesThisPhase ?? G.cargoTransportLevel;

    if (player.turnsCompletedThisPhase >= maxDeliveries) {
      debug.log(`[moveGoodsSeattlePhase] Phase ending - deliveries: ${player.turnsCompletedThisPhase}/${maxDeliveries}`);
      return true;
    }

    return false;
  },

  onEnd: ({ G }: { G: SeattleGameState }) => {
    const player = G.players['0'];
    debug.log(`[moveGoodsSeattlePhase] Phase 5 ended, deliveries: ${player.turnsCompletedThisPhase}`);
  },

  // Phase 6 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: SeattleGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'collectIncome';
  }
};
